import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  EmptyState,
  Flex,
  LoadingOrbit,
  PageHeader,
  Plus,
  Text,
  useToast,
} from '@chatsdet/ui';
import { UsersThree, Warning } from '@phosphor-icons/react';
import { API_URL } from '../../../../config';
import { ApiError, toApiError } from '../../../../lib/api-error';
import { toastErrorOptionsFor } from '../../../../lib/toast-error-options';
import { fetchOrgPropertyById } from '../../../../hooks/useOrgProperty';
import {
  useCredentialTemplates,
  useDeleteCredentialTemplate,
  useLinkCredentialToTemplate,
  useUnlinkCredentialFromTemplate,
  type CredentialTemplate,
} from '../../../../hooks/useCredentialTemplates';
import { CredentialListItem } from '../../CredentialListItem';
import { CredentialTemplateDialog } from '../../CredentialTemplateDialog';
import { PersonaGroup } from './PersonaGroup';
import { UnassignedSection } from './UnassignedSection';
import type { TestUserCredential } from './types';

type PersonaDialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; template: CredentialTemplate };

type Props = {
  propertyId: string;
  /** Open the credential dialog; templateId pre-binds (and locks) the persona. */
  onAddCredential: (opts?: { templateId?: string }) => void;
  onEditCredential: (credId: string) => void;
};

/**
 * Property-scoped "Test Users" tab: personas (credential templates) with
 * their per-environment credential bindings, plus unassigned credentials.
 * Credentials are bound/unbound by dragging rows between groups, or via the
 * persona select in the credential form.
 */
export function PropertyTestUsersTab({ propertyId, onAddCredential, onEditCredential }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [showEphemeral, setShowEphemeral] = React.useState(false);
  const [deleteConfirmCred, setDeleteConfirmCred] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [personaDialog, setPersonaDialog] = React.useState<PersonaDialogState>({ mode: 'closed' });
  const [pendingDeletePersona, setPendingDeletePersona] = React.useState<CredentialTemplate | null>(
    null
  );
  const [activeCred, setActiveCred] = React.useState<TestUserCredential | null>(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const credsQ = useQuery({
    queryKey: ['property-creds', propertyId],
    queryFn: async (): Promise<{ items: TestUserCredential[] }> => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/credentials`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch credentials');
      return r.json();
    },
  });

  const environmentsQ = useQuery({
    queryKey: ['property-environments', propertyId],
    queryFn: async (): Promise<{ items: Array<{ id: string; name: string }> }> => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/environments`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch environments');
      return r.json();
    },
  });

  // Cached by the parent PropertyDetail page under the same key.
  const propertyQ = useQuery({
    queryKey: ['property', propertyId],
    queryFn: async () => {
      return fetchOrgPropertyById<{ id: string; name: string }>(propertyId);
    },
  });

  const templatesQ = useCredentialTemplates(propertyId);
  const templates = templatesQ.data ?? [];

  // ── Derived state ──────────────────────────────────────────────────────────

  const environmentNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const environment of environmentsQ.data?.items ?? []) {
      map.set(environment.id, environment.name);
    }
    return map;
  }, [environmentsQ.data?.items]);

  const allCreds = React.useMemo(
    () =>
      (credsQ.data?.items ?? []).map((cred) => ({
        ...cred,
        environmentName: cred.environmentId
          ? (environmentNameById.get(cred.environmentId) ?? cred.environmentId)
          : null,
      })),
    [credsQ.data?.items, environmentNameById]
  );

  const hasEphemeral = allCreds.some((c) => c.isEphemeral);
  const visibleCreds = React.useMemo(
    () => (showEphemeral ? allCreds : allCreds.filter((c) => !c.isEphemeral)),
    [allCreds, showEphemeral]
  );

  const { byTemplate, unassigned } = React.useMemo(() => {
    const byTemplate = new Map<string, TestUserCredential[]>();
    const unassigned: TestUserCredential[] = [];
    for (const c of visibleCreds) {
      if (c.templateId) {
        const arr = byTemplate.get(c.templateId) ?? [];
        arr.push(c);
        byTemplate.set(c.templateId, arr);
      } else {
        unassigned.push(c);
      }
    }
    return { byTemplate, unassigned };
  }, [visibleCreds]);

  const refreshCredentials = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ['property-creds', propertyId] });
    qc.invalidateQueries({ queryKey: ['org-credentials-with-properties'] });
  }, [qc, propertyId]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const linkMut = useLinkCredentialToTemplate(propertyId);
  const unlinkMut = useUnlinkCredentialFromTemplate(propertyId);
  const deleteTemplateMut = useDeleteCredentialTemplate(propertyId);

  const clearStorageState = useMutation({
    mutationFn: async (credId: string) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credentials/${credId}/storage-state`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      refreshCredentials();
      toast.success('Cleared saved session');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to clear saved session');
    },
  });

  const deleteCred = useMutation({
    mutationFn: async (credId: string) => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/credentials/${credId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      refreshCredentials();
      toast.success('Credential deleted successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete credential');
    },
  });

  const depsQ = useQuery({
    queryKey: ['credential-deps', deleteConfirmCred?.id],
    queryFn: async (): Promise<{ workflows: Array<{ id: string; name: string }> }> => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credentials/${deleteConfirmCred!.id}/dependencies`,
        { credentials: 'include' }
      );
      if (!r.ok) throw new Error('Failed to check dependencies');
      return r.json();
    },
    enabled: !!deleteConfirmCred,
  });

  // ── Drag and drop ──────────────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as { credentialId?: string } | undefined;
      const cred = allCreds.find((c) => c.id === data?.credentialId) ?? null;
      setActiveCred(cred);
    },
    [allCreds]
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      setActiveCred(null);
      const { active, over } = event;
      if (!over) return;

      const data = active.data.current as
        | { credentialId: string; sourceTemplateId: string | null }
        | undefined;
      if (!data) return;

      const overId = String(over.id);
      if (!overId.startsWith('persona:')) return;
      const targetTemplateId = overId === 'persona:none' ? null : overId.slice('persona:'.length);
      if (targetTemplateId === data.sourceTemplateId) return;

      const handleError = (err: unknown) => {
        if (err instanceof ApiError && err.code === 'CREDENTIAL_TEMPLATE_ENV_ALREADY_BOUND') {
          toast.error(
            'That environment already has a credential bound to this persona',
            toastErrorOptionsFor(err)
          );
        } else {
          toast.error(
            err instanceof Error ? err.message : 'Failed to move credential',
            toastErrorOptionsFor(err)
          );
        }
      };

      if (targetTemplateId === null) {
        // Dropped on "Unassigned" — unlink from the source persona.
        if (!data.sourceTemplateId) return;
        unlinkMut.mutate(
          { templateId: data.sourceTemplateId, credentialId: data.credentialId },
          { onError: handleError, onSettled: refreshCredentials }
        );
      } else {
        // Linking overwrites templateId on the credential row, so a
        // persona-to-persona move is a single link call.
        linkMut.mutate(
          { templateId: targetTemplateId, credentialId: data.credentialId },
          { onError: handleError, onSettled: refreshCredentials }
        );
      }
    },
    [linkMut, unlinkMut, refreshCredentials, toast]
  );

  // ── Persona actions ────────────────────────────────────────────────────────

  const handleDeletePersona = (template: CredentialTemplate) => {
    deleteTemplateMut.mutate(template.id, {
      onSuccess: () => {
        toast.success(`Deleted persona "${template.name}"`);
        setPendingDeletePersona(null);
        refreshCredentials();
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Delete failed'),
    });
  };

  const handleUnlink = (cred: TestUserCredential) => {
    if (!cred.templateId) return;
    unlinkMut.mutate(
      { templateId: cred.templateId, credentialId: cred.id },
      {
        onSuccess: () => toast.success(`Unlinked "${cred.name}"`),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Unlink failed'),
        onSettled: refreshCredentials,
      }
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const isLoading = credsQ.isLoading || templatesQ.isLoading;
  const isEmpty = !isLoading && templates.length === 0 && visibleCreds.length === 0;

  return (
    <Box className="space-y-4">
      <PageHeader
        size="section"
        title="Test Users"
        description="Personas group per-environment credentials so a test resolves the right login automatically."
        actions={
          <Flex align="center" gap="md">
            {hasEphemeral && (
              <Checkbox
                isSelected={showEphemeral}
                onChange={setShowEphemeral}
                label="Show ephemeral"
              />
            )}
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Plus size={14} />}
              onPress={() => setPersonaDialog({ mode: 'create' })}
            >
              New persona
            </Button>
          </Flex>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingOrbit size="md" />
        </div>
      ) : isEmpty ? (
        <EmptyState
          icon={<UsersThree size={24} />}
          title="Set up your first test user"
          description={
            <div className="space-y-2">
              <Text size="sm" className="text-muted-foreground block">
                Test users let your flows sign in automatically so you can exercise authenticated
                pages.
              </Text>
              <div className="text-left space-y-2">
                <Text size="sm" className="text-muted-foreground">
                  <Text as="span" weight="medium" className="text-foreground">
                    Credential
                  </Text>
                  {' — a stored login (email + password, SSO, etc.) for this application.'}
                </Text>
                <Text size="sm" className="text-muted-foreground">
                  <Text as="span" weight="medium" className="text-foreground">
                    Persona
                  </Text>
                  {
                    ' — groups credentials across environments (staging, prod, sandbox) so a single test resolves the right login automatically.'
                  }
                </Text>
              </div>
            </div>
          }
          action={
            <Button
              variant="primary"
              iconLeft={<Plus size={16} />}
              onPress={() => setPersonaDialog({ mode: 'create' })}
            >
              New persona
            </Button>
          }
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveCred(null)}
        >
          <div className="space-y-3">
            {templates.map((template) => (
              <PersonaGroup
                key={template.id}
                propertyId={propertyId}
                template={template}
                credentials={byTemplate.get(template.id) ?? []}
                onAddCredential={() => onAddCredential({ templateId: template.id })}
                onEditPersona={() => setPersonaDialog({ mode: 'edit', template })}
                onDeletePersona={() => setPendingDeletePersona(template)}
                onEditCredential={onEditCredential}
                onDeleteCredential={(cred) =>
                  setDeleteConfirmCred({ id: cred.id, name: cred.name })
                }
                onClearStorageState={(credId) => clearStorageState.mutate(credId)}
                onUnlink={handleUnlink}
                onRefresh={refreshCredentials}
              />
            ))}

            <UnassignedSection
              propertyId={propertyId}
              credentials={unassigned}
              hasPersonas={templates.length > 0}
              onEditCredential={onEditCredential}
              onDeleteCredential={(cred) => setDeleteConfirmCred({ id: cred.id, name: cred.name })}
              onClearStorageState={(credId) => clearStorageState.mutate(credId)}
              onRefresh={refreshCredentials}
            />
          </div>

          <DragOverlay
            dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}
          >
            {activeCred ? (
              <div className="rounded-lg border border-border bg-card shadow-lg">
                <CredentialListItem propertyId={propertyId} credential={activeCred} variant="row" />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Persona create/edit */}
      {personaDialog.mode !== 'closed' && (
        <CredentialTemplateDialog
          propertyId={propertyId}
          propertyName={propertyQ.data?.name ?? ''}
          isOpen
          onOpenChange={(open) => {
            if (!open) setPersonaDialog({ mode: 'closed' });
          }}
          template={personaDialog.mode === 'edit' ? personaDialog.template : null}
        />
      )}

      {/* Persona delete confirmation */}
      {pendingDeletePersona && (
        <DialogTrigger
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingDeletePersona(null);
          }}
        >
          <span />
          <Dialog title="Delete Persona">
            <DialogContent>
              <Text>
                Delete persona{' '}
                <Text as="span" weight="bold">
                  {pendingDeletePersona.name}
                </Text>
                ?
              </Text>
              <Text size="sm" className="text-muted-foreground mt-2">
                Linked credentials will remain; they'll just become unassigned.
              </Text>
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onPress={() => setPendingDeletePersona(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={() => handleDeletePersona(pendingDeletePersona)}
                isDisabled={deleteTemplateMut.isPending}
              >
                {deleteTemplateMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>
      )}

      {/* Credential delete confirmation (with dependency check) */}
      <DialogTrigger
        isOpen={!!deleteConfirmCred}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDeleteConfirmCred(null);
            deleteCred.reset();
          }
        }}
      >
        <span />
        <Dialog title="Delete Credential">
          <DialogContent>
            <Box className="space-y-3">
              <Text>
                Are you sure you want to delete <strong>{deleteConfirmCred?.name}</strong>? This
                action cannot be undone.
              </Text>
              {depsQ.isLoading ? (
                <Flex align="center" justify="center" className="py-2">
                  <LoadingOrbit size="sm" />
                </Flex>
              ) : (depsQ.data?.workflows?.length ?? 0) > 0 ? (
                <Flex
                  direction="column"
                  gap="sm"
                  className="p-3 rounded-md bg-warning/10 border border-warning/30"
                >
                  <Flex align="center" gap="sm" className="text-warning">
                    <Warning size={20} weight="fill" />
                    <Text size="sm" className="font-medium">
                      This credential is used in {depsQ.data!.workflows.length}{' '}
                      {depsQ.data!.workflows.length === 1 ? 'flow' : 'flows'}:
                    </Text>
                  </Flex>
                  <ul className="list-disc list-inside pl-1 space-y-0.5">
                    {depsQ.data!.workflows.map((w) => (
                      <li key={w.id}>
                        <Text size="sm" as="span">
                          {w.name}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </Flex>
              ) : null}
              {deleteCred.isError && (
                <Flex
                  align="center"
                  gap="sm"
                  className="p-3 rounded-md bg-destructive/10 text-destructive"
                >
                  <Warning size={20} />
                  <Text size="sm">
                    {deleteCred.error?.message || 'Failed to delete credential'}
                  </Text>
                </Flex>
              )}
            </Box>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => {
                setDeleteConfirmCred(null);
                deleteCred.reset();
              }}
              isDisabled={deleteCred.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={() => {
                if (deleteConfirmCred) {
                  deleteCred.mutate(deleteConfirmCred.id, {
                    onSuccess: () => setDeleteConfirmCred(null),
                  });
                }
              }}
              isDisabled={deleteCred.isPending}
            >
              {deleteCred.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
